// Support contact helpers
// ------------------------
// Central place for the support email address and for building a pre-populated
// "request support" mailto link. We pre-fill the subject and a structured body
// (screen, URL, time, user/org, browser) so that when a clinician emails
// support, we already have the context needed to help them without a
// back-and-forth. The subject is deliberately "Requesting Support" rather than
// "Reporting Issue" because this in-app flow is also used for questions and
// feedback, not only bugs. Pre-sign-in screens keep their own "report an
// issue" mailto because those links are specifically for people stuck at auth.

export const SUPPORT_EMAIL = 'support@thinkdivergent.co.uk';

// Human-friendly ordinal suffix for a day-of-month (1st, 2nd, 3rd, 4th...).
const ordinalSuffix = (day) => {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

/**
 * Build a `mailto:` href to support, pre-filled with a friendly subject and a
 * body containing diagnostic context. Opening it launches the user's default
 * email client with everything ready except their description of the request.
 *
 * All context fields are optional; anything missing is simply omitted so the
 * email never contains empty/placeholder lines.
 *
 * @param {Object} [context]
 * @param {string} [context.userId]          Firebase Auth UID of the current user
 * @param {string} [context.organisation]    Organisation name
 * @param {string} [context.patientId]         UID of the patient being worked on (patient routes only)
 * @param {string} [context.referenceNumber]   Patient's reference number (patient routes only)
 * @param {string} [context.assessmentPathway] Human-readable pathway label e.g. "Adult ADHD" (patient routes only)
 * @param {string} [context.errorMessage]      A current error message, if any
 * @param {string} [context.supportCode]       A support/error code, if any
 * @returns {string} a fully-encoded mailto: href
 */
export const buildSupportEmailHref = ({
  userId,
  organisation,
  patientId,
  referenceNumber,
  assessmentPathway,
  errorMessage,
  supportCode,
} = {}) => {
  const subject = 'Requesting Support with ThinkDivergent';

  const now = new Date();
  const day = now.getDate();
  const easyTime =
    `${day}${ordinalSuffix(day)} ${now.toLocaleString('en-GB', { month: 'long' })} `
    + `${now.getFullYear()} at `
    + `${now.toLocaleString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  const isoTime = now.toISOString();

  const screen = typeof window !== 'undefined' ? window.location.pathname : '';
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const browser = typeof navigator !== 'undefined' ? navigator.userAgent : '';

  const lines = [
    'To ThinkDivergent Support Team,',
    '',
    'I would like to request support with the platform.',
    '',
    '[Please describe briefly what you were doing and what happened]',
    '',
    'Helpful Information:',
    `- Screen: ${screen}`,
    `- URL: ${url}`,
    `- Time: ${easyTime} (${isoTime})`,
    userId ? `- User ID: ${userId}` : undefined,
    organisation ? `- Organisation: ${organisation}` : undefined,
    patientId ? `- Patient ID: ${patientId}` : undefined,
    referenceNumber ? `- Reference Number: ${referenceNumber}` : undefined,
    assessmentPathway ? `- Assessment Pathway: ${assessmentPathway}` : undefined,
    supportCode ? `- Support/Error code: ${supportCode}` : undefined,
    errorMessage ? `- Message: ${errorMessage}` : undefined,
    `- Browser: ${browser}`,
  ].filter((v) => v != null).join('\n');

  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines)}`;
};
